import * as React from 'react';
import {ClientSummary, ErrorResponse, PageResponseEnvelope} from '../api/dtos/dtos';
import {RouteComponentProps, withRouter} from 'react-router';
import {ColumnProps} from 'antd/lib/table';
import locales from '../locales';
import * as paths from '../paths';
import {Button, Switch, Table} from 'antd';
import {ErrorAlert} from '../common/ErrorAlert';
import furoApi from '../api/FuroApi';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableColumn, searchableSortableColumn} from '../utils/table-utils';
import * as routerUtils from '../utils/router-utils';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import ButtonBar from '../common/ButtonBar';
import accountProvider from "../account/AccountProvider";
import ClientName from "../common/ClientName";

interface ClientsPageState extends GridState<ClientSummary> {
  error?: ErrorResponse;
  showDeletedOnly: boolean;
}

class ClientsPage extends React.Component<RouteComponentProps, ClientsPageState> {

  public state: ClientsPageState = {
    showDeletedOnly: false,
    // Default initial grid state
    ...tableUtils.initialGridState(),

    // Initial sorting
    sortColumnKey: 'name',
    sortOrder: 'ascend',

    // Override with cached state from Router
    ...this.props.location.state,
  };

  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const {showDeletedOnly} = this.state;

    const columns: Array<ColumnProps<ClientSummary>> = [
      searchableSortableColumn(locales.name, 'name', this.state, this.renderNameColumn),
      searchableSortableColumn(locales.zip, 'zip', this.state, r => r.zip),
      searchableSortableColumn(locales.city, 'city', this.state, r => r.city),
      searchableSortableColumn(locales.street_house_number, 'address', this.state, r => r.address),
      searchableColumn(locales.responsible_user, 'responsibleUser', this.state, r => r.responsibleUser),
      searchableSortableColumn(locales.comment, 'comment', this.state, r => r.comment),
    ];

    const isAuthorized = accountProvider.isAdmin() || accountProvider.isSales();

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        {isAuthorized &&
            <ButtonBar style={{marginBottom: 8}}>
                <>
                    <span style={{marginRight: 4}}>{locales.show_only_deleted}</span>
                    <Switch checked={showDeletedOnly} onChange={this.onShowDeletedOnlyChange}/>
                </>
                <Button
                    size="small" type="primary" icon="plus"
                    disabled={showDeletedOnly}
                    onClick={this.onAddButtonClick}>{locales.add}
                </Button>
            </ButtonBar>
        }

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 1024}}
        />

        <ErrorAlert style={{marginTop: '16px'}} error={this.state.error}/>
      </>
    );
  }

  private renderNameColumn = (r: ClientSummary): React.ReactNode => {
    return (
      <ClientName
        name={r.name}
        link={paths.viewClientPath(r.id)}
        warning={r.warning}
      />
    );
  };

  private onGridChange = (newState: GridState<ClientSummary>) => {
    this.setState({...newState}, () => this.reload());
  };

  private onAddButtonClick = () => {
    this.props.history.push(paths.createClientPath);
  };

  private reload = () => {
    const request = {
      ...tableUtils.toGridRequest(this.state),
      deleted: this.state.showDeletedOnly
    }
    const promise = furoApi.clients.getAll(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<ClientSummary>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    }, () => {
      routerUtils.cacheComponentStateForLocation(this.props, this.state);
    });
  };

  private onShowDeletedOnlyChange = (checked: boolean) => {
    this.setState({
        showDeletedOnly: checked,
        page: 1,
      },
      this.reload);
  };


  private onApiError = (error: ErrorResponse) => {
    this.setState({error})
  };

}

export default withRouter(ClientsPage);


// WEBPACK FOOTER //
// ./src/client/ClientsPage.tsx